// Tablero "qué sale mañana": lo que está comprometido a salir en una fecha, tomado de los
// RENGLONES DE PEDIDO de SAP. La entrega de mañana todavía no existe (se crea al despachar),
// así que esto no sale de nuestra cola: se consulta SAP en vivo.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import { STATUS_CLASS, STATUS_LABEL, WAREHOUSES, listPlanned } from '@/services/deliveries'
import type { PlannedOrder } from '@/types'

/** Fecha local en YYYY-MM-DD (toISOString daría UTC y después de las 6pm cambiaría de día). */
function isoLocal(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function shiftDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return isoLocal(d)
}

const tomorrow = () => shiftDays(isoLocal(new Date()), 1)

export default function PlannedBoard() {
  const { user } = useAuth()
  const fixedWarehouse = user?.role === 'almacen' ? user?.warehouse ?? null : null
  const [date, setDate] = useState(tomorrow)
  const [warehouse, setWarehouse] = useState('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['planned', date, warehouse],
    queryFn: () => listPlanned(date, warehouse || undefined),
  })

  const orders = data ?? []
  const byWarehouse = orders.reduce<Record<string, PlannedOrder[]>>((acc, o) => {
    const k = o.warehouse ?? 'Sin almacén'
    ;(acc[k] ??= []).push(o)
    return acc
  }, {})
  const lines = orders.reduce((n, o) => n + o.lines.length, 0)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Qué sale mañana"
        subtitle="Renglones de pedido comprometidos a esta fecha · directo de SAP"
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setDate(shiftDays(date, -1))}
            className="rounded-lg border border-gray-300 px-2.5 py-2 text-sm hover:bg-gray-50"
            aria-label="Día anterior"
          >
            ‹
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            onClick={() => setDate(shiftDays(date, 1))}
            className="rounded-lg border border-gray-300 px-2.5 py-2 text-sm hover:bg-gray-50"
            aria-label="Día siguiente"
          >
            ›
          </button>
        </div>
        {date !== tomorrow() && (
          <button
            onClick={() => setDate(tomorrow())}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
          >
            Mañana
          </button>
        )}
        {fixedWarehouse ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700">
            🏬 Almacén {fixedWarehouse}
          </span>
        ) : (
          <select
            value={warehouse}
            onChange={(e) => setWarehouse(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todos los almacenes</option>
            {WAREHOUSES.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-gray-500">Consultando SAP…</p>
      ) : isError ? (
        <p className="rounded-xl bg-red-50 p-6 text-center text-sm text-red-800 ring-1 ring-red-100">
          No se pudo consultar SAP. Intenta de nuevo en un momento.
        </p>
      ) : !orders.length ? (
        <p className="rounded-xl bg-white p-8 text-center text-gray-500 ring-1 ring-black/5">
          Nada programado para el {date}.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            {orders.length} pedido(s) · {lines} renglón(es) por surtir
          </p>
          {Object.entries(byWarehouse).map(([wh, group]) => (
            <section key={wh} className="space-y-2">
              <h2 className="flex items-baseline gap-2 pt-2 text-sm font-bold text-gray-900">
                🏬 {wh}
                <span className="text-xs font-normal text-gray-500">
                  {group.length} pedido(s) · {group.reduce((n, o) => n + o.lines.length, 0)} renglón(es)
                </span>
              </h2>
              {group.map((o) => (
                <OrderCard key={`${o.order_entry}-${o.warehouse}`} o={o} />
              ))}
            </section>
          ))}
        </>
      )}
    </div>
  )
}

function OrderCard({ o }: { o: PlannedOrder }) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">
            Pedido #{o.order_num}
            {o.num_at_card && (
              <span className="ml-2 text-xs font-normal text-gray-500">ref. {o.num_at_card}</span>
            )}
          </p>
          <p className="truncate text-sm text-gray-700">{o.card_name ?? '—'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {o.deliveries.length ? (
            o.deliveries.map((d) => (
              <Link
                key={d.id}
                to={`/entregas/${d.id}`}
                className={`rounded-full px-2 py-0.5 text-xs font-medium hover:underline ${STATUS_CLASS[d.status]}`}
              >
                Entrega #{d.doc_num} · {STATUS_LABEL[d.status]}
              </Link>
            ))
          ) : (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              Sin entrega generada
            </span>
          )}
        </div>
      </div>

      <table className="mt-3 w-full border-t border-gray-100 text-sm">
        <tbody>
          {o.lines.map((l, i) => (
            <tr key={`${l.item_code}-${i}`} className="border-b border-gray-50 last:border-0">
              <td className="w-24 py-1.5 pr-2 align-top font-mono text-xs text-gray-500">
                {l.item_code}
              </td>
              <td className="py-1.5 pr-2 align-top text-gray-800">{l.description ?? '—'}</td>
              <td className="w-16 py-1.5 text-right align-top font-semibold tabular-nums text-gray-900">
                {l.quantity}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
